/**
 * TabContent — Active tab editing area.
 *
 * Uses the existing NoteEditor (contentEditable-based) for rich text.
 * Shows tab title, word count, last-edited timestamp.
 */
import React, { useCallback, useMemo } from 'react';
import NoteEditor from './NoteEditor';

export default function TabContent({ tab, onContentChange, saveStatus, errorMsg, onManualSave, companyId }) {
    const content = tab?.content || {};
    console.log('[TabContent] Rendering tab:', tab?.tabName);
    console.log('[TabContent] Content length:', content.richContent?.length || 0, 'chars');

    const editorValue = useMemo(() => {
        const value = {
            html: content.richContent || '',
            plainText: content.plainText || '',
        };
        console.log('[TabContent] Editor value prepared:', {
            htmlLength: value.html.length,
            plainTextLength: value.plainText.length
        });
        return value;
    }, [content.richContent, content.plainText]);

    const wordCount = useMemo(() => {
        const text = content.plainText || '';
        return text.trim() ? text.trim().split(/\s+/).length : 0;
    }, [content.plainText]);

    const handleEditorChange = useCallback(({ html, plainText }) => {
        onContentChange?.({
            richContent: html,
            plainText,
        });
    }, [onContentChange]);

    if (!tab) {
        return (
            <div className="nw-tab-content nw-tab-content--empty">
                <p style={{ color: 'var(--muted)' }}>Select a tab to start writing</p>
            </div>
        );
    }

    const lastEdited = content.lastEdited
        ? new Date(content.lastEdited).toLocaleString()
        : null;

    /* Save status indicator text */
    const statusText = {
        idle: '',
        saving: 'Saving…',
        saved: 'Saved ✓',
        error: errorMsg || 'Save failed',
    }[saveStatus] || '';

    return (
        <div className="nw-tab-content">
            {/* ── Tab info bar ── */}
            <div className="nw-tab-info">
                <span className="nw-tab-info-name">{tab.tabName}</span>
                <span className="nw-tab-info-meta">
                    {wordCount > 0 && <span>{wordCount} words</span>}
                    {lastEdited && <span>Last edited: {lastEdited}</span>}
                </span>
                {statusText && (
                    <span className={`nw-save-status nw-save-status--${saveStatus}`}>
                        {statusText}
                    </span>
                )}
                {saveStatus === 'error' && onManualSave && (
                    <button
                        type="button"
                        className="nw-save-retry-btn"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onManualSave(); }}
                    >
                        Retry
                    </button>
                )}
            </div>

            {/* ── Editor ── */}
            <div className="nw-editor-wrap">
                <NoteEditor
                    value={editorValue}
                    onChange={handleEditorChange}
                    placeholder={`Start writing ${tab.tabName}...`}
                    autoFocus
                    companyId={companyId}
                    tabId={tab?.tabId}
                />
            </div>
        </div>
    );
}
